import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    X, GitCommit, Sparkles, Loader2, Check, ArrowLeft,
    AlertTriangle, FileText, RotateCcw,
} from 'lucide-react';
import { toast } from '../ui/Toast';
import MarkdownMessage from '../chat/MarkdownMessage';
import {
    estimateDistill, distillSession, consolidateSession,
} from '../../services/consolidationApi';

// ─── États de la machine ────────────────────────────────────────
const STEPS = {
    CHOOSING: 'choosing_model',
    GENERATING: 'generating',
    PREVIEW: 'preview',
    CONFIRMING: 'confirming',
    APPLYING: 'applying',
    DONE: 'done',
    ERROR: 'error',
};

const MODEL_OPTIONS = [
    { slug: 'haiku', label: 'Cl. Haiku 4.5', tagline: 'Rapide et économique', recommended: false },
    { slug: 'sonnet', label: 'Cl. Sonnet 4.6', tagline: 'Équilibre qualité/coût', recommended: true },
    { slug: 'opus', label: 'Cl. Opus 4.6', tagline: 'Synthèse haute densité', recommended: false },
];

// Timeouts adaptatifs (ms)
const TIMEOUTS = { haiku: 45000, sonnet: 75000, opus: 90000 };

const REASSURE_MESSAGES = [
    'Analyse des messages…',
    'Séparation de l\'intention et du processus…',
    'Identification des idées retenues et abandonnées…',
    'Structuration de la synthèse…',
];

const formatCost = (v) => {
    if (v == null) return '—';
    if (v < 0.01) return `$${v.toFixed(4)}`;
    return `$${v.toFixed(3)}`;
};

const ConsolidateModal = ({ projectId, sessionRef, sessionTitle, onClose, onDone }) => {
    const [step, setStep] = useState(STEPS.CHOOSING);
    const [errorMsg, setErrorMsg] = useState('');
    const [estimate, setEstimate] = useState(null);
    const [loadingEstimate, setLoadingEstimate] = useState(true);
    const [selectedModel, setSelectedModel] = useState('sonnet');
    const [includeFiles, setIncludeFiles] = useState(false);
    const [distillResult, setDistillResult] = useState(null);
    const [editedMarkdown, setEditedMarkdown] = useState('');
    const [reassureIdx, setReassureIdx] = useState(0);
    const [slowWarning, setSlowWarning] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const slowTimerRef = useRef(null);
    const reassureTimerRef = useRef(null);

    // Charge l'estimation à l'ouverture
    useEffect(() => {
        setLoadingEstimate(true);
        estimateDistill(projectId, sessionRef)
            .then(data => {
                setEstimate(data);
                if (data.already_consolidated) {
                    setErrorMsg(
                        `Cette conversation a déjà été consolidée. Va dans l'onglet Paramètres du projet, section "Historique de consolidation" : tu pourras y consulter la synthèse ou faire un rollback avant de re-consolider.`
                    );
                    setStep(STEPS.ERROR);
                    return;
                }
                if (!data.meets_threshold) {
                    setErrorMsg(`Cette conversation contient ${data.total_count} messages. Au moins ${data.min_messages_required} sont nécessaires pour consolider.`);
                    setStep(STEPS.ERROR);
                }
            })
            .catch(err => {
                setErrorMsg(err.message || 'Impossible d\'estimer les coûts.');
                setStep(STEPS.ERROR);
            })
            .finally(() => setLoadingEstimate(false));
    }, [projectId, sessionRef]);

    // Rotation des messages rassurants pendant la génération
    useEffect(() => {
        if (step === STEPS.GENERATING) {
            reassureTimerRef.current = setInterval(() => {
                setReassureIdx(i => (i + 1) % REASSURE_MESSAGES.length);
            }, 2500);
            slowTimerRef.current = setTimeout(() => setSlowWarning(true), 45000);
        } else {
            clearInterval(reassureTimerRef.current);
            clearTimeout(slowTimerRef.current);
            setSlowWarning(false);
            setReassureIdx(0);
        }
        return () => {
            clearInterval(reassureTimerRef.current);
            clearTimeout(slowTimerRef.current);
        };
    }, [step]);

    const handleGenerate = async () => {
        setStep(STEPS.GENERATING);
        try {
            const timeoutMs = TIMEOUTS[selectedModel];
            const generatePromise = distillSession(projectId, sessionRef, {
                model: selectedModel, includeFiles,
            });
            const timeoutPromise = new Promise((_, rej) =>
                setTimeout(() => rej(new Error('Timeout — la synthèse prend plus de temps que prévu. Réessaye avec un modèle plus léger.')), timeoutMs)
            );
            const data = await Promise.race([generatePromise, timeoutPromise]);
            setDistillResult(data);
            setEditedMarkdown(data.synthesis_markdown || '');
            setStep(STEPS.PREVIEW);
        } catch (err) {
            setErrorMsg(err.message || 'Erreur lors de la génération.');
            setStep(STEPS.ERROR);
        }
    };

    const handleValidate = () => {
        if (!editedMarkdown || editedMarkdown.trim().length < 50) {
            toast.warning('Synthèse trop courte pour être consolidée.');
            return;
        }
        setStep(STEPS.CONFIRMING);
    };

    const handleConsolidate = async () => {
        if (!confirmed) return;
        setStep(STEPS.APPLYING);
        try {
            const result = await consolidateSession(projectId, sessionRef, {
                synthesisEdited: editedMarkdown,
                modelUsed: distillResult?.model_used,
                costUsd: distillResult?.cost_usd,
            });
            setApplyResult(result);
            setStep(STEPS.DONE);
            toast.success(`Conversation consolidée — ${result.messages_archived} message(s) archivé(s).`);
        } catch (err) {
            setErrorMsg(err.message || 'Erreur lors de la consolidation.');
            setStep(STEPS.ERROR);
        }
    };

    const estimateDisplay = useMemo(() => {
        if (!estimate?.estimates) return null;
        return estimate.estimates;
    }, [estimate]);

    const wordCount = useMemo(() => {
        if (!editedMarkdown) return 0;
        return editedMarkdown.trim().split(/\s+/).filter(Boolean).length;
    }, [editedMarkdown]);

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{
                width: '92vw', maxWidth: step === STEPS.PREVIEW ? '1100px' : '560px',
                maxHeight: '92vh', display: 'flex', flexDirection: 'column',
                background: 'var(--card-bg)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '20px', overflow: 'hidden',
                boxShadow: '0 0 40px rgba(29,78,216,0.18), 0 0 80px rgba(29,78,216,0.06)',
                transition: 'max-width 0.3s ease',
            }}>
                {/* Header */}
                <div style={{
                    padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, rgba(29,78,216,0.10), rgba(139,92,246,0.08))',
                    borderBottom: '1px solid rgba(59,130,246,0.14)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: 38, height: 38, borderRadius: '10px',
                            background: 'rgba(59,130,246,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <GitCommit size={20} style={{ color: '#3b82f6' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
                                Consolider la conversation
                            </div>
                            {sessionTitle && (
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', maxWidth: '380px',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {sessionTitle}
                                </div>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-secondary)', padding: '6px', borderRadius: '8px',
                    }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>

                    {/* ─────────── CHOOSING ─────────── */}
                    {step === STEPS.CHOOSING && (
                        <>
                            {loadingEstimate ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                                    <div style={{ marginTop: 12, color: 'var(--text-secondary)' }}>Analyse de la conversation…</div>
                                </div>
                            ) : (
                                <>
                                    <div style={{
                                        padding: '12px 14px', marginBottom: 16, borderRadius: 10,
                                        background: 'rgba(59,130,246,0.06)',
                                        border: '1px solid rgba(59,130,246,0.18)',
                                        fontSize: 13, lineHeight: 1.55, color: 'var(--text-primary)',
                                    }}>
                                        <strong>{estimate?.messages_count} message(s)</strong> seront analysés.
                                        La synthèse sera générée puis <strong>relue et éditable</strong> avant validation.
                                        Rien n'est modifié tant que tu ne valides pas.
                                        {estimate?.truncated && (
                                            <div style={{ marginTop: 6, fontSize: 12, color: '#f59e0b' }}>
                                                Conversation longue : premier + derniers échanges gardés (détails inédits au milieu peuvent être allégés).
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
                                        Choisis le modèle de synthèse
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {MODEL_OPTIONS.map(opt => {
                                            const est = estimateDisplay?.[opt.slug];
                                            const selected = selectedModel === opt.slug;
                                            return (
                                                <label key={opt.slug} style={{
                                                    display: 'flex', alignItems: 'center', gap: 12,
                                                    padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                                    background: selected ? 'rgba(59,130,246,0.10)' : 'transparent',
                                                    border: selected ? '1px solid rgba(59,130,246,0.35)' : '1px solid var(--border-color)',
                                                    transition: 'all 0.15s',
                                                }}>
                                                    <input type="radio" name="model" checked={selected}
                                                        onChange={() => setSelectedModel(opt.slug)}
                                                        style={{ accentColor: '#3b82f6' }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                                                            {opt.label}
                                                            {opt.recommended && (
                                                                <span style={{
                                                                    marginLeft: 8, fontSize: 10, padding: '2px 6px',
                                                                    background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                                                                    borderRadius: 6, fontWeight: 600,
                                                                }}>RECOMMANDÉ</span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{opt.tagline}</div>
                                                    </div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6', minWidth: 70, textAlign: 'right' }}>
                                                        {est ? formatCost(est.cost_usd) : '—'}
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: 10, marginTop: 16,
                                        padding: '10px 14px', borderRadius: 10,
                                        background: 'transparent', border: '1px solid var(--border-color)',
                                        cursor: 'pointer', fontSize: 13,
                                    }}>
                                        <input type="checkbox" checked={includeFiles} onChange={e => setIncludeFiles(e.target.checked)}
                                            style={{ accentColor: '#3b82f6' }} />
                                        <span style={{ color: 'var(--text-primary)' }}>Inclure les résumés des fichiers liés</span>
                                    </label>

                                    <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
                                        <button onClick={onClose} style={btnSecondary}>Annuler</button>
                                        <button onClick={handleGenerate} style={btnPrimary}>
                                            <Sparkles size={14} /> Générer la synthèse
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ─────────── GENERATING ─────────── */}
                    {step === STEPS.GENERATING && (
                        <div style={{ textAlign: 'center', padding: '48px 0' }}>
                            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                            <div style={{ marginTop: 18, color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>
                                {REASSURE_MESSAGES[reassureIdx]}
                            </div>
                            {slowWarning && (
                                <div style={{
                                    marginTop: 18, padding: '10px 14px', borderRadius: 10,
                                    background: 'rgba(245,158,11,0.08)',
                                    border: '1px solid rgba(245,158,11,0.25)',
                                    fontSize: 12, color: '#f59e0b', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto',
                                }}>
                                    {MODEL_OPTIONS.find(m => m.slug === selectedModel)?.label} prend un peu plus de temps sur une conversation dense, c'est normal.
                                </div>
                            )}
                            <div style={{ marginTop: 24 }}>
                                <button onClick={onClose} style={btnSecondary}>Annuler</button>
                            </div>
                        </div>
                    )}

                    {/* ─────────── PREVIEW ─────────── */}
                    {step === STEPS.PREVIEW && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                padding: '10px 14px', borderRadius: 10,
                                background: 'rgba(59,130,246,0.06)',
                                border: '1px solid rgba(59,130,246,0.18)',
                            }}>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Modèle :</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{distillResult?.model_used}</span>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>•</span>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Coût : <strong style={{ color: 'var(--text-primary)' }}>{formatCost(distillResult?.cost_usd)}</strong></span>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>•</span>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{wordCount} mots</span>
                                {distillResult?.truncated && (
                                    <span style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(245,158,11,0.12)',
                                        color: '#f59e0b', borderRadius: 6 }}>Conversation tronquée</span>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, minHeight: '45vh' }}>
                                {/* Éditeur */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                                        color: 'var(--text-secondary)', fontWeight: 600 }}>Édition (markdown)</div>
                                    <textarea value={editedMarkdown} onChange={e => setEditedMarkdown(e.target.value)}
                                        style={{
                                            flex: 1, minHeight: '45vh',
                                            background: 'var(--input-bg)',
                                            border: '1px solid var(--border-color)', borderRadius: 10,
                                            padding: 12, fontSize: 13, lineHeight: 1.6,
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            color: 'var(--text-primary)', resize: 'vertical',
                                        }} />
                                </div>
                                {/* Aperçu */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                                        color: 'var(--text-secondary)', fontWeight: 600 }}>Aperçu rendu</div>
                                    <div style={{
                                        flex: 1, minHeight: '45vh', overflow: 'auto',
                                        background: 'var(--input-bg)',
                                        border: '1px solid var(--border-color)', borderRadius: 10,
                                        padding: 14, fontSize: 13, lineHeight: 1.6,
                                        color: 'var(--text-primary)',
                                    }}>
                                        <MarkdownMessage content={editedMarkdown} disableMath={true} />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <button onClick={() => setStep(STEPS.CHOOSING)} style={btnSecondary}>
                                    <ArrowLeft size={14} /> Retour
                                </button>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button onClick={onClose} style={btnSecondary}>Annuler</button>
                                    <button onClick={handleValidate} style={btnPrimary}>
                                        <GitCommit size={14} /> Consolider définitivement
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ─────────── CONFIRMING ─────────── */}
                    {step === STEPS.CONFIRMING && (
                        <>
                            <div style={{
                                padding: 16, borderRadius: 12,
                                background: 'rgba(245,158,11,0.08)',
                                border: '1px solid rgba(245,158,11,0.25)',
                                display: 'flex', gap: 12, marginBottom: 16,
                            }}>
                                <AlertTriangle size={20} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Confirmer la consolidation</div>
                                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                                        <li><strong>{distillResult?.messages_count || 0} messages</strong> seront archivés (récupérables pendant 90 jours).</li>
                                        <li>La synthèse deviendra un <strong>fichier du projet</strong>, indexée sémantiquement.</li>
                                        <li>Le résumé long-terme sera régénéré sur la base nettoyée.</li>
                                        <li>Les insights trans-projets dérivés seront marqués pour revalidation.</li>
                                    </ul>
                                </div>
                            </div>
                            <label style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                                border: '1px solid var(--border-color)', fontSize: 13,
                            }}>
                                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
                                    style={{ accentColor: '#3b82f6' }} />
                                <span style={{ color: 'var(--text-primary)' }}>J'ai relu la synthèse et je valide la consolidation.</span>
                            </label>

                            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                                <button onClick={() => { setConfirmed(false); setStep(STEPS.PREVIEW); }} style={btnSecondary}>
                                    <ArrowLeft size={14} /> Retour édition
                                </button>
                                <button onClick={handleConsolidate} disabled={!confirmed} style={{
                                    ...btnPrimary,
                                    opacity: confirmed ? 1 : 0.4,
                                    cursor: confirmed ? 'pointer' : 'not-allowed',
                                }}>
                                    <GitCommit size={14} /> Consolider
                                </button>
                            </div>
                        </>
                    )}

                    {/* ─────────── APPLYING ─────────── */}
                    {step === STEPS.APPLYING && (
                        <div style={{ textAlign: 'center', padding: '48px 0' }}>
                            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                            <div style={{ marginTop: 16, color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>
                                Application de la consolidation…
                            </div>
                            <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>
                                Archivage des messages, création du fichier synthèse, régénération de la mémoire.
                            </div>
                        </div>
                    )}

                    {/* ─────────── DONE ─────────── */}
                    {step === STEPS.DONE && (
                        <div style={{ textAlign: 'center', padding: '32px 8px' }}>
                            <div style={{
                                width: 56, height: 56, margin: '0 auto 16px',
                                borderRadius: '50%',
                                background: 'rgba(34,197,94,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Check size={28} style={{ color: '#22c55e' }} />
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                                Conversation consolidée
                            </div>
                            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                                <strong>{applyResult?.messages_archived}</strong> message(s) archivé(s).
                                Synthèse créée comme fichier du projet.
                            </div>
                            {applyResult?.needs_revalidation_count > 0 && (
                                <div style={{ marginTop: 10, fontSize: 12, color: '#f59e0b' }}>
                                    {applyResult.needs_revalidation_count} insight(s) trans-projet marqué(s) pour revalidation.
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
                                <button onClick={onClose} style={btnSecondary}>Fermer</button>
                                <button
                                    onClick={() => {
                                        onDone?.({
                                            synthesisFileId: applyResult?.synthesis_file_id,
                                            consolidationId: applyResult?.consolidation_id,
                                        });
                                        onClose();
                                    }}
                                    style={btnPrimary}
                                >
                                    <FileText size={14} /> Ouvrir la synthèse
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ─────────── ERROR ─────────── */}
                    {step === STEPS.ERROR && (
                        <div style={{ padding: '24px 8px', textAlign: 'center' }}>
                            <AlertTriangle size={28} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                                Une erreur est survenue
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)', maxWidth: 420, margin: '8px auto 0' }}>
                                {errorMsg}
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
                                <button onClick={onClose} style={btnSecondary}>Fermer</button>
                                {step === STEPS.ERROR && distillResult == null && estimate?.meets_threshold && (
                                    <button onClick={() => { setErrorMsg(''); setStep(STEPS.CHOOSING); }} style={btnPrimary}>
                                        <RotateCcw size={14} /> Réessayer
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

// ─── Boutons réutilisables ───
const btnBase = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
};
const btnPrimary = {
    ...btnBase,
    background: 'linear-gradient(135deg, #1D4ED8, #3b82f6)',
    color: 'white',
    boxShadow: '0 4px 14px rgba(29,78,216,0.28)',
};
const btnSecondary = {
    ...btnBase,
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
};

export default ConsolidateModal;
